<template>
  <section class="section">
    <div class="columns is-centered">
      <div class="column">
        <h3 class="title is-4 level">
          <div class="level-left">
            <div class="level-item">
                <span v-if="$route.name == 'AddProduct'">{{ $t('addProduct.title') }}</span>
                <span v-if="$route.name == 'EditProduct'">{{ $t('addProduct.editTitle')}}{{ product.id }}</span>
            </div>
          </div>
          <div class="level-right">
            <div class="level-item">
                <!-- IF in edit mode then text should be save not save draft -->
                <draft-button v-if="$route.name == 'AddProduct'" :label="$t('app.buttons.saveDraft')" class="is-fullwidth" @click="onSaveDraft"></draft-button>
                <update-button v-if="$route.name == 'EditProduct'" :label="$t('app.buttons.update')" class="is-fullwidth" @click="onSaveDraft"></update-button>
            </div>
            <div class="level-item">
              <publish-button :label="$t('app.buttons.publish')" class="is-fullwidth" v-if="!product.isPublished" @click="onPublish"></publish-button>
              <unpublish-button :label="$t('app.buttons.unpublish')" class="is-fullwidth" v-if="product.isPublished" @click="onUnpublish"></unpublish-button>
            </div>
          </div>
        </h3>
        <success-notification :onClosed="() => view.successNotification = ''" v-if="view.successNotification">{{ view.successNotification }}</success-notification>
        <failure-notification :onClosed="() => view.failureNotification = ''" v-if="view.failureNotification">{{ view.failureNotification }}</failure-notification>
        <hr>
        <div class="columns">
          <div class="column">
            <div class="field">
              <label class="label">{{ $t('addProduct.fields.name') }}*</label>
              <p class="control has-icons-left has-icons-right">
                <input v-focusonempty class="input" type="text" v-model="product.name" required :placeholder="$t('addProduct.fields.namePlaceholder')"
                  v-bind:class="{
                    'is-danger': (view.onSaveErrors || view.onPublishErrors) && !product.name,
                  }" />
                <span class="icon is-small is-left">
                  <i class="fa fa-edit"></i>
                </span>
              </p>
            </div>
          </div>
        </div>
        <hr>
        <div class="columns">
          <div class="column">
            <div class="field">
              <label class="label">{{ $t('addProduct.fields.price') }}*</label>
              <p class="control has-icons-left has-icons-right">
                <input class="input" type="number" v-model="product.price" step="0.1"
                v-bind:class="{
                    'is-danger': (view.onSaveErrors || view.onPublishErrors) && !product.price
                  }" />
                <span class="icon is-small is-left">
                  <i class="fa fa-euro"></i>
                </span>
              </p>
              <p class="help is-info" v-if="vendor && vendor.b2b === true">
                {{ $t('addProduct.fields.vatExcluded') }}
              </p>
            </div>
          </div>
          <div class="column">
            <div class="field">
              <label class="label">{{ $t('addProduct.fields.discount') }}</label>
              <div class="control has-icons-left">
                <div class="select is-fullwidth">
                  <select v-model="product.discount">
                    <option value=""></option>
                    <option v-for="discount in view.discounts" :key="discount.id" :value="discount.id">{{discount.name}}</option>
                  </select>
                </div>
                <div class="icon is-small is-left">
                  <i class="fa fa-tag"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <hr>
        <div class="columns">
          <div class="column">
            <label class="label">{{ $t('addProduct.fields.netWeight') }}*</label>
            <div class="field has-addons">
              <p class="control">
                <input class="input is-fullwidth" type="number" required v-model="product.netWeight" max="10" step="0.1"
                  v-bind:class="{
                      'is-danger': (view.onSaveErrors || view.onPublishErrors) && (!product.netWeight || product.netWeight <= 0),
                    }" />
              </p>
              <p class="control">
                <span class="select">
                  <select v-model="product.netWeightUnit">
                    <option value=""></option>
                    <option v-for="unit in view.units" :key="unit.id" :value="unit.id">{{ unit.name }}</option>
                  </select>
                </span>
              </p>
            </div>
            <p class="help is-info">
              {{ $t('addProduct.fields.netWeightHelp') }}
            </p>
          </div>
          <div class="column">
            <label class="label">{{ $t('addProduct.fields.grossWeight') }}*</label>
            <div class="field has-addons">
              <p class="control">
                <input class="input is-fullwidth" type="number" required v-model="product.grossWeight" max="10" step="0.1"
                  v-bind:class="{
                      'is-danger': (view.onSaveErrors || view.onPublishErrors) && (!product.grossWeight || product.grossWeight <= 0),
                    }" />
              </p>
              <p class="control">
                <a class="button is-static">
                  {{ $t('app.units.kg') }}
                </a>
              </p>
            </div>
            <p class="help is-info">
              {{ $t('addProduct.fields.grossWeightHelp') }}
            </p>
          </div>
        </div>
        <hr>
        <div class="columns">
          <div class="column">
            <div class="field">
              <label class="label">{{ $t('addProduct.fields.categories') }}</label>
                <span class="select is-multiple is-fullwidth">
                  <select multiple size="10" v-model="product.categories">
                    <option v-for="category in view.categories" :key="category.id" :value="category.id">{{ category.secondLevel }} - {{ category.thirdLevel }}</option>
                  </select>
                </span>
            </div>
          </div>
        </div>
        <hr>
        <div class="columns">
          <div class="column">
            <div class="field">
              <label class="label">{{ $t('addProduct.fields.description') }}</label>
              <textarea class="textarea" v-model="product.description" :placeholder="$t('addProduct.fields.descriptionPlaceholder')"></textarea>
            </div>
          </div>
        </div>
        <hr>
        <div class="columns">
          <div class="column">
            <div class="field">
              <label class="label">{{ $t('addProduct.fields.ingredients') }}</label>
              <textarea class="textarea" v-model="product.ingredients" :placeholder="$t('addProduct.fields.ingredientsPlaceholder')"></textarea>
            </div>
          </div>
        </div>
        <hr>
        <div class="columns">
          <div class="column">
            <div class="field">
              <label class="label">{{ $t('addProduct.fields.image') }}</label>
              <ImageUpload @image-uploaded = "setImage($event)" :edit="$route.name == 'EditProduct'" :id="product.imageId" />
            </div>
          </div>
        </div>
        <hr>
        <div class="columns">
          <!-- <div class="column">
            <label class="label">{{ $t('addProduct.fields.certifications') }}</label>
            <div class="field">
              <p class="control">
                <span class="select is-fullwidth">
                  <select v-model="product.certifications">
                    <option value=""></option>
                    <option v-for="certification in view.certifications" :key="certification.id" :value="certification.id">{{ certification.name }}</option>
                  </select>
                </span>
              </p>
            </div>
          </div> -->
          <div class="column">
            <label class="label">{{ $t('addProduct.fields.producer') }}</label>
            <div class="field">
              <p class="control">
                <input class="input" type="text" v-model="product.producer" :placeholder="$t('addProduct.fields.producerPlaceholder')" />
              </p>
            </div>
          </div>
        </div>
        <hr>
        <div class="columns">
          <div class="column">
            <label class="label">{{ $t('addProduct.fields.origin') }}</label>
            <div class="field">
              <p class="control">
                <span class="select is-fullwidth">
                  <select v-model="product.origin">
                    <option value=""></option>
                    <option v-for="origin in view.origins" :key="origin.id" :value="origin.id">{{ origin.countryName }}</option>
                  </select>
                </span>
              </p>
            </div>
          </div>
          <div class="column">
            <label class="label">{{ $t('addProduct.fields.subOrigin') }}</label>
            <div class="field">
              <p class="control">
                <span class="select is-fullwidth">
                  <select v-model="product.subOrigin" :disabled="!product.origin " :class="{ disabled: !product.origin }">
                    <option value=""></option>
                    <option v-for="suborigin in view.suborigins" :key="suborigin.id" :value="suborigin.id">{{suborigin.regionName}}</option>
                  </select>
                </span>
              </p>
            </div>
          </div>
        </div>
        <hr>
        <div class="columns">
          <div class="column">
            <div class="level-right">
              <div class="level-item">
                <draft-button v-if="$route.name == 'AddProduct'" :label="$t('app.buttons.saveDraft')" class="is-fullwidth" @click="onSaveDraft"></draft-button>
                <update-button v-if="$route.name == 'EditProduct'" :label="$t('app.buttons.update')" class="is-fullwidth" @click="onSaveDraft"></update-button>
              </div>
              <div class="level-item">
                <publish-button :label="$t('app.buttons.publish')" class="is-fullwidth" v-if="!product.isPublished" @click="onPublish"></publish-button>
                <unpublish-button :label="$t('app.buttons.unpublish')" class="is-fullwidth" v-if="product.isPublished" @click="onUnpublish"></unpublish-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { FooshiApi } from '@/services/fooshiApi.service'

import FormSection from '@/components/forms/FormSection'
import UpdateButton from '@/components/forms/UpdateButton'
import DraftButton from '@/components/forms/DraftButton'
import PublishButton from '@/components/forms/PublishButton'
import UnpublishButton from '@/components/forms/UnpublishButton'
import EditButton from '@/components/forms/EditButton'
import DeleteButton from '@/components/forms/DeleteButton'
import StatusTag from '@/components/forms/StatusTag.vue'
import HtmlImage from '../../components/html/HtmlImage.vue'
import HtmlTag from '@/components/forms/HtmlTag.vue'
import ImageUpload from '@/components/ImageUpload.vue'
import SuccessNotification from '../../components/SuccessNotification.vue'
import FailureNotification from '../../components/FailureNotification.vue'
import Constants from '../../constants'

export default {
  name: 'AddProduct',
  components: {
    FormSection,
    UpdateButton,
    DraftButton,
    PublishButton,
    UnpublishButton,
    EditButton,
    DeleteButton,
    HtmlTag,
    StatusTag,
    HtmlImage,
    ImageUpload,
    SuccessNotification,
    FailureNotification
  },
  methods: {
    invalid () {
      if (!this.product.name ||
        !this.product.price || this.product.price <= 0 ||
        !this.product.netWeight || this.product.netWeight <= 0 ||
        !this.product.grossWeight || this.product.grossWeight <= 0) {
        return this.$t('addProduct.errors.mandatoryFields')
      }

      if (this.product.grossWeight > 10) {
        return this.$t('addProduct.errors.grossWeightMaxValue')
      }

      return false
    },
    async onPublish () {
      this.view.successNotification = ''
      this.view.failureNotification = ''
      this.view.onPublishErrors = false

      const invalidMessage = this.invalid()
      if (!invalidMessage) {
        const productToPublish = { ...this.product, isPublished: true }
        const response = this.$route.name === 'AddProduct' ? await FooshiApi.addProduct(productToPublish) : await FooshiApi.updateProduct(productToPublish)
        if (response.success) {
          this.$router.push({ name: 'Products' })
        } else {
          this.handleApiError(response)
        }
      } else {
        this.view.failureNotification = invalidMessage
        this.view.onPublishErrors = true
      }
    },
    async onUnpublish () {
      // get un-edited version of the product
      const unpublishedProduct = {
        ...this.readonlyProduct,
        isPublished: false
      }
      const response = await FooshiApi.updateProduct(unpublishedProduct)
      if (response.success) {
        // todo: redirect with or without save changes?
        this.$router.push({ name: 'Products' })
      } else {
        this.handleApiError(response)
      }
    },
    async onSaveDraft () {
      this.view.successNotification = ''
      this.view.failureNotification = ''
      this.view.onSaveErrors = false

      const invalidMessage = this.invalid()
      if (!invalidMessage) {
        const response = this.$route.name === 'AddProduct' ? await FooshiApi.addProduct(this.product) : await FooshiApi.updateProduct(this.product)
        if (response.success) {
          this.$router.push({ name: 'Products' })
        } else {
          this.handleApiError(response)
        }
      } else {
        this.view.failureNotification = invalidMessage
        this.view.onSaveErrors = true
      }
    },
    async fetchProduct () {
      var response = await FooshiApi.getProduct(this.$route.params.id)
      if (response.success) {
        const fetchedProduct = response.data
        this.product = {
          ...fetchedProduct,
          categories: fetchedProduct.categories.map(category => category.id)
        }
        this.readonlyProduct = {
          ...this.product
        }

        this.setSuborigins()
      } else {
        // todo: toast
      }
    },
    async fetchMetaData () {
      var response = await FooshiApi.getProductProperties()
      if (response.success) {
        const metaData = response.data
        this.view.categories = metaData.categories
        this.view.categories.sort((a, b) => {
          var displayName = a.secondLevel
          var displayNameB = b.secondLevel
          return displayName.localeCompare(displayNameB)
        })

        this.view.origins = metaData.origins
        this.view.origins.sort((a, b) => {
          return a.countryName.localeCompare(b.countryName)
        })

        this.view.suborigins = []

        this.view.suboriginsUnfiltered = metaData.suborigins
        this.view.suboriginsUnfiltered.sort((a, b) => {
          return a.regionName.localeCompare(b.regionName)
        })
      }
    },
    async fetchVendor () {
      try {
        var response = await FooshiApi.getVendor()
        if (response.success) {
          this.vendor = response.data
        }
      } catch (error) {
        // Handle error silently or log it
      }
    },
    handleApiError (response) {
      if (Array.isArray(response.message)) {
        response.message.map(e => {
          this.view.failureNotification += this.$t(e)
        })
      } else {
        this.view.failureNotification = this.$t(response.message)
      }
    },
    setImage (imageId) {
      this.product.imageId = imageId
    },
    setSuborigins () {
      if (this.selectedOrigin) {
        var selected = this.view.origins.find(el => el.id === this.selectedOrigin)
        this.view.suborigins = this.view.suboriginsUnfiltered.filter(el => el.countryName === selected.countryName)
      } else {
        this.view.suborigins.length = 0
      }
    }
  },
  async mounted () {
    await this.fetchMetaData()
    await this.fetchVendor()
    if (this.$route.name === 'EditProduct') {
      await this.fetchProduct()
    }
  },
  computed: {
    selectedOrigin () {
      return this.product.origin
    }
  },
  watch: {
    selectedOrigin (newOrigin, oldOrigin) {
      this.setSuborigins()
    }
  },
  data () {
    return {
      view: {
        onSaveErrors: false,
        onPublishErrors: false,
        ready: true,
        categories: [
          { id: 1, name: 'Food' },
          { id: 2, name: 'Drink' }
        ],
        origins: [],
        suborigins: [
          { id: 1, name: 'East' },
          { id: 2, name: 'West' }
        ],
        suboriginsUnfiltered: [
          { id: 1, name: 'East' },
          { id: 2, name: 'West' }
        ],
        producers: [
          { id: 1, name: 'Unilever' },
          { id: 2, name: 'Nestle' }
        ],
        certifications: [
          { id: 1, name: 'ISO123' },
          { id: 2, name: 'HeartHealthy' }
        ],
        discounts: [
          { id: 1, name: '-10%' },
          { id: 2, name: '-15%' },
          { id: 3, name: '-20%' }
        ],
        units: Constants.UNITS,
        successNotification: '',
        failureNotification: ''
      },
      readonlyProduct: {},
      vendor: null,
      product: {
        id: this.$route.params.id,
        name: '',
        description: '',
        ingredients: '',
        price: undefined,
        priceCurrency: 'EUR',
        discount: 0,
        imageId: undefined,
        images: [], // todo: fix upload button
        categories: [],
        netWeight: undefined,
        netWeightUnit: 'kg',
        grossWeight: undefined,
        grossWeightUnit: 'kg',
        isPublished: false
      }
    }
  }
}
</script>

<style>
.sticky {
  position: fixed;
  z-index: 1000;
}
</style>



// WEBPACK FOOTER //
// src/views/products/AddProduct.vue